import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Dna } from 'lucide-react';

/**
 * Reusable morph/genetics selector with autocomplete.
 * Dynamic suggestions by species. Supports free-text entry.
 * Emits: { display_name, canonical_slug }
 */
export default function MorphSelect({
  speciesCanonical,
  value,        // display_name
  onChange,      // (display_name) => void
  className = '',
}) {
  const [morphData, setMorphData] = useState(null);
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const wrapperRef = useRef(null);

  // Fetch morph suggestions when species changes
  useEffect(() => {
    if (!speciesCanonical) {
      setMorphData(null);
      setSuggestions([]);
      return;
    }
    api.getMorphsBySpecies(speciesCanonical)
      .then((data) => {
        setMorphData(data);
        setSuggestions(data.morphs || []);
      })
      .catch(() => setMorphData(null));
  }, [speciesCanonical]);

  // Sync external value changes
  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  // Filter suggestions based on input
  const filterSuggestions = useCallback((text) => {
    if (!morphData?.morphs) return [];
    if (!text.trim()) return morphData.morphs;
    const lower = text.toLowerCase();
    return morphData.morphs.filter((m) =>
      m.display_name.toLowerCase().includes(lower) ||
      m.canonical_slug.includes(lower.replace(/\s+/g, '-'))
    );
  }, [morphData]);

  const handleInputChange = (e) => {
    const raw = e.target.value;
    setInputValue(raw);
    setSuggestions(filterSuggestions(raw));
    setShowDropdown(true);
    setFocusIndex(-1);
  };

  const selectMorph = (morph) => {
    setInputValue(morph.display_name);
    onChange(morph.display_name);
    setShowDropdown(false);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowDropdown(false);
      if (inputValue.trim()) {
        // Title Case normalize on blur
        const normalized = inputValue.trim().split(' ')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' ');
        setInputValue(normalized);
        onChange(normalized);
      } else {
        onChange('');
      }
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && focusIndex >= 0) {
      e.preventDefault();
      selectMorph(suggestions[focusIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const clearValue = () => {
    setInputValue('');
    onChange('');
    setShowDropdown(false);
  };

  if (!speciesCanonical) return null;

  return (
    <div className={`relative ${className}`} ref={wrapperRef} data-testid="morph-select">
      <Label className="text-zinc-400 mb-2 block">
        <Dna className="w-3.5 h-3.5 inline mr-1.5" />
        Genetica / Morph
      </Label>
      <div className="relative">
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => { setSuggestions(filterSuggestions(inputValue)); setShowDropdown(true); }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Buscar o escribir morph..."
          className="bg-white/5 border-white/10 text-zinc-100 pr-8"
          data-testid="morph-input"
        />
        {inputValue && (
          <button
            type="button"
            onClick={clearValue}
            className="absolute right-2 top-2.5 text-zinc-500 hover:text-zinc-300"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {showDropdown && suggestions.length > 0 && (
        <div
          className="absolute z-50 w-full mt-1 max-h-56 overflow-y-auto rounded-lg border border-white/10 bg-zinc-900/95 backdrop-blur-sm shadow-xl"
          data-testid="morph-suggestions"
        >
          {suggestions.map((morph, idx) => (
            <button
              key={morph.canonical_slug}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectMorph(morph)}
              className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between hover:bg-white/5 ${
                idx === focusIndex ? 'bg-white/10' : ''
              }`}
            >
              <span className="text-zinc-200">{morph.display_name}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-500">
                {morph.status === 'official' ? 'oficial' :
                 morph.status === 'approved' ? 'aprobado' :
                 morph.status === 'community' ? 'comunidad' : 'nuevo'}
                {morph.breeder_used ? ' · tuyo' : ''}
              </span>
            </button>
          ))}
        </div>
      )}

      {inputValue && !suggestions.find(m => m.display_name === inputValue) && morphData?.morphs?.length > 0 && (
        <p className="text-xs text-amber-400/70 mt-1">Nuevo morph — se guardara y normalizara automaticamente</p>
      )}
    </div>
  );
}
